import { selectableEntranceAnimationOptions } from 'js/config/animationOptions';
import {
  ENTRANCE_ANIMATIONS_WITH_CURSOR,
  ENTRANCE_ANIMATIONS_WITH_DIRECTIONS,
  ENTRANCE_STAGE_KEY,
  EXIT_ANIMATIONS_WITH_CURSOR,
  EXIT_STAGE_KEY,
  PAUSE_STAGE_KEY
} from 'js/config/consts';
import {
  AnimationConfiguration,
  ElementConfigurationKey,
  trackAnimationSettingsChanged
} from 'js/reducers/userAnimationSettings';
import getAllCursors from 'js/selectors/getAllCursors';
import { RootState } from 'js/types';
import capitalize from 'lodash.capitalize';
import { call, CallEffect, select, SelectEffect, takeEvery } from 'redux-saga/effects';

import { track } from '../mixpanelProvider';

function* trackSettingsChanged(): Generator<SelectEffect | CallEffect, void, RootState> {
  const state = yield select();
  const settings = state.userAnimationSettings.stageSettings;
  const cursors = getAllCursors(state);

  const trackingProperties: { [key: string]: unknown } = {};
  Object.entries(settings).forEach(([stage, settings]) => {
    const stageLabel = capitalize(stage);
    trackingProperties[`${stageLabel} Settings Applied To`] = settings.applyTo;

    const configKeys: ElementConfigurationKey[] = settings.applyTo === 'all' ? ['All'] : ['Image', 'Shape', 'Text'];

    configKeys.forEach(configKey => {
      const propertyPrefix = configKey === 'All' ? stageLabel : `${stageLabel} ${configKey}`;

      // Always track duration for all stages (both regular and pause)
      trackingProperties[`${propertyPrefix} Animation Duration (seconds)`] =
        settings.elementTypeSettings[configKey].duration;

      // Only track animation type for non-pause stages
      if (stage !== PAUSE_STAGE_KEY) {
        // Safe to cast since we know it's not PAUSE_STAGE_KEY
        const animConfig = settings.elementTypeSettings[configKey] as AnimationConfiguration;

        trackingProperties[`${propertyPrefix} Animation Type`] = animConfig.animationType;

        if (
          (stage === ENTRANCE_STAGE_KEY && ENTRANCE_ANIMATIONS_WITH_CURSOR.includes(animConfig.animationType)) ||
          (stage === EXIT_STAGE_KEY && EXIT_ANIMATIONS_WITH_CURSOR.includes(animConfig.animationType))
        ) {
          const cursorId = animConfig.configuration.cursorId;
          const cursor = cursors.find(cursor => cursor.id === cursorId);

          trackingProperties[`${propertyPrefix} Cursor ID`] = cursorId;
          trackingProperties[`${propertyPrefix} Cursor Name`] = cursor?.name;
          trackingProperties[`${propertyPrefix} Cursor Category`] = cursor?.group;
        }

        if (stage === ENTRANCE_STAGE_KEY && ENTRANCE_ANIMATIONS_WITH_DIRECTIONS.includes(animConfig.animationType)) {
          const directionName = selectableEntranceAnimationOptions.find(
            opt => opt.value === animConfig.configuration.direction
          );
          trackingProperties[`${propertyPrefix} Animation Direction`] =
            directionName?.label ?? animConfig.configuration.direction;
        }
      }
    });
  });

  yield call(track, `Default Settings Updated`, trackingProperties);
}

export default function* trackUserAnimationSettings() {
  yield takeEvery(trackAnimationSettingsChanged.type, trackSettingsChanged);
}
